import { useCallback } from 'react';
import classNames from 'classnames';
import { useDividerDrag, useKeyboardStep } from './hooks';
import style from './horizontal-draggable-divider.module.scss';

interface HorizontalDraggableDividerProps {
  onDrag: (widthPx: number) => void;
  minWidth: number;
  maxWidth: number;
  /** Which side of the handle the resized panel is on. */
  side?: 'left' | 'right';
  /** The panel's current width. Supplying it makes the handle focusable and
   *  arrow-key operable, which needs a value to step from. */
  value?: number;
  /** The separator's accessible name. */
  label?: string;
  className?: string;
}

/** Pixels moved per arrow press. */
const KEYBOARD_STEP_PX = 16;

/**
 * A vertical drag handle that resizes the panel beside it, reporting the new
 * width within bounds.
 * @param {HorizontalDraggableDividerProps} props The handle.
 * @return {JSX.Element} The handle.
 */
function HorizontalDraggableDivider({
  onDrag,
  minWidth,
  maxWidth,
  side = 'right',
  value,
  label = 'Resize panel',
  className,
}: HorizontalDraggableDividerProps) {
  const onMove = useCallback(
    ({ clientX }: { clientX: number }, parent: DOMRect) => {
      const raw =
        side === 'left' ? clientX - parent.left : parent.right - clientX;
      onDrag(Math.max(minWidth, Math.min(maxWidth, raw)));
    },
    [onDrag, minWidth, maxWidth, side]
  );
  const { ref, dragging, handlers } = useDividerDrag('col-resize', onMove);
  // A panel on the handle's left grows as the handle moves right.
  const onKeyDown = useKeyboardStep({
    value,
    min: minWidth,
    max: maxWidth,
    step: KEYBOARD_STEP_PX,
    decrease: side === 'left' ? 'ArrowLeft' : 'ArrowRight',
    increase: side === 'left' ? 'ArrowRight' : 'ArrowLeft',
    onChange: onDrag,
  });
  const keyboard = value !== undefined;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a focusable separator with a
          value is ARIA's window splitter, an interactive widget; jsx-a11y counts every separator as static. */}
      <div
        ref={ref}
        className={classNames(style.divider, className)}
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={keyboard ? Math.round(value) : undefined}
        aria-valuemin={keyboard ? minWidth : undefined}
        aria-valuemax={keyboard ? maxWidth : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the window splitter above
        tabIndex={keyboard ? 0 : undefined}
        onKeyDown={onKeyDown}
        {...handlers}
      >
        <div className={style.handle} />
      </div>
      {dragging && <div className={style.dragOverlay} />}
    </>
  );
}

export { HorizontalDraggableDivider };
